import type { TaskSummary } from '@fos/shared/contracts/work';
import {
  TASK_AI_STATE_LABELS,
  TASK_AI_STATE_TONES,
  TASK_PRIORITY_LABELS,
  TASK_PRIORITY_TONES,
  TASK_STATUS_LABELS,
  TASK_STATUS_TONES,
} from '@fos/shared/labels';
import { Check, Flag, MessageSquareText, Sparkles, UserRound } from 'lucide-react';
import { Fragment, type ReactNode } from 'react';
import { useSession } from '../../../lib/auth';
import { cn } from '../../../lib/cn';
import { Avatar } from '../../../ui/Avatar';
import { Button } from '../../../ui/Button';
import { ClientChip, ProjectChip } from '../../../ui/EntityLabel';
import { Status } from '../../../ui/Status';
import { type PillTone, StatusPill } from '../../../ui/StatusPill';
import { TOUCH_BUTTON } from '../../mobile';
import { type DueInfo, dueInfo, dueToneClass } from '../lib/due';

/** Kontekst zadania w jednej linii: klient (albo projekt wewnętrzny), np. „REA Nieruchomości”, „Fravity OS”. */
export function contextLabel(task: Pick<TaskSummary, 'client' | 'project'>): string | null {
  return task.client?.name ?? task.project?.name ?? null;
}

/** Pełny kontekst: klient · projekt albo usługa. */
export function contextLong(task: Pick<TaskSummary, 'client' | 'project' | 'service'>): string | null {
  const parts = [task.client?.name, task.project?.name ?? task.service?.name].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : null;
}

/**
 * Chipy klienta i projektu (lifting 17.10.2026, DECISIONS „Zadania łatwiejsze w użyciu”): klient w stałej barwie
 * (ui/EntityLabel + lib/entity-color), projekt albo usługa — neutralny chip. `client={false}` — bez klienta
 * (np. w jego własnym profilu), `project={false}` — bez projektu.
 */
export function TaskChips({
  task,
  client = true,
  project = true,
  wrap = false,
  className,
}: {
  task: Pick<TaskSummary, 'client' | 'project' | 'service'>;
  client?: boolean;
  project?: boolean;
  /** chipy w kolejnych liniach zamiast skracania (wąskie karty tablicy, telefon) */
  wrap?: boolean;
  className?: string;
}) {
  const projectName = task.project?.name ?? task.service?.name ?? null;
  const showClient = client && task.client;
  const showProject = project && projectName;
  if (!showClient && !showProject) return null;
  return (
    <span className={cn('flex min-w-0 items-center gap-1', wrap && 'flex-wrap', className)}>
      {showClient && task.client && <ClientChip client={task.client} />}
      {showProject && projectName && <ProjectChip name={projectName} />}
    </span>
  );
}

/** Termin w kolorze znaczenia: po terminie czerwony, dziś bursztynowy (DECISIONS 17.10.2026). */
export function DueText({ due, className }: { due: DueInfo | null; className?: string }) {
  if (!due) return null;
  return (
    <span data-due-tone={due.tone} className={cn('shrink-0 whitespace-nowrap', dueToneClass(due), className)}>
      {due.text}
    </span>
  );
}

/**
 * Linia meta karty / wiersza (Figma: „REA · czw.”): chipy klienta i projektu, termin w kolorze znaczenia
 * i dodatki (`extra`, np. stan AI, „zlecone”). `context` (tekst) zostaje dla miejsc bez chipów.
 */
export function TaskMetaLine({
  task,
  today,
  context,
  chips = false,
  extra,
  className,
}: {
  task: Pick<TaskSummary, 'client' | 'project' | 'service' | 'dueDate' | 'status'>;
  today: string;
  /** tekst kontekstu zamiast chipów (np. „od AI”); domyślnie klient albo projekt */
  context?: string | null;
  /** chipy klienta i projektu zamiast tekstu kontekstu (lifting 17.10.2026) */
  chips?: boolean;
  extra?: ReactNode;
  className?: string;
}) {
  const due = dueInfo(task.dueDate, today, { done: task.status === 'done' });
  const text = chips ? null : context === undefined ? contextLabel(task) : context;
  const hasChips = chips && Boolean(task.client || task.project || task.service);
  const parts: ReactNode[] = [];
  if (text)
    parts.push(
      <span key="ctx" className="min-w-0 truncate">
        {text}
      </span>,
    );
  if (due) parts.push(<DueText key="due" due={due} />);
  if (parts.length === 0 && !extra && !hasChips) return null;
  return (
    <span
      className={cn(
        'flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 text-caption text-fg-muted',
        !chips && 'flex-nowrap',
        className,
      )}
    >
      {hasChips && <TaskChips task={task} className="max-w-full" />}
      {parts.map((p, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: stała kolejność części
        <Fragment key={i}>
          {i > 0 && <span aria-hidden>·</span>}
          {p}
        </Fragment>
      ))}
      {extra}
    </span>
  );
}

/** Zalogowana osoba (id) — do etykiety „zlecone”; poza sesją null. */
export function useMeId(): string | null {
  const session = useSession().data;
  return session?.authenticated ? session.user.id : null;
}

/** Zadanie, które dodałem i przypisałem komuś innemu (etykieta „zlecone”, filtr „Zlecone przeze mnie”; 10.10.2026). */
export function isDelegatedByMe(task: Pick<TaskSummary, 'createdByUserId' | 'assignee'>, me: string | null): boolean {
  return Boolean(me && task.createdByUserId === me && task.assignee && task.assignee.userId !== me);
}

/**
 * Osoba przy zadaniu (10.10.2026, lista, tablica, zadania w projekcie i u klienta): awatar z inicjałami w kolorze
 * osoby — jak w menu; zadanie bez osoby = neutralny znacznik (przerywane kółko). Imię w podpowiedzi i dla czytnika.
 */
export function AssigneeAvatar({ assignee, className }: { assignee: TaskSummary['assignee']; className?: string }) {
  if (!assignee) {
    return (
      <span
        role="img"
        aria-label="Bez osoby"
        title="Bez osoby"
        className={cn(
          'inline-flex size-6 shrink-0 items-center justify-center rounded-full border border-dashed border-line-strong text-fg-muted',
          className,
        )}
      >
        <UserRound size={12} strokeWidth={1.75} aria-hidden />
      </span>
    );
  }
  return (
    <span title={assignee.name} className={cn('inline-flex shrink-0', className)}>
      <Avatar initials={assignee.initials} color={assignee.avatarColor} title={assignee.name} />
    </span>
  );
}

/** Dyskretna etykieta przy zadaniu zleconym innej osobie. */
export function DelegatedLabel({ className }: { className?: string }) {
  return <span className={cn('shrink-0 whitespace-nowrap text-caption text-fg-muted', className)}>zlecone</span>;
}

/** Stan AI („AI pracuje”, „Wynik AI czeka”…) jako pigułka z ikoną AI; `none` niewidoczny. */
export function AiStatus({ state, className }: { state: TaskSummary['aiState']; className?: string }) {
  if (state === 'none') return null;
  return (
    <StatusPill tone={TASK_AI_STATE_TONES[state]} icon={Sparkles} className={className}>
      {TASK_AI_STATE_LABELS[state]}
    </StatusPill>
  );
}

/**
 * Barwa pigułki statusu zadania (lifting 17.10.2026): Do zrobienia — neutralna, W toku — akcent, Zablokowane —
 * „czeka” (bursztyn), Do odbioru — „do sprawdzenia” (fiolet), Gotowe — zieleń. Status spoza mapy (np. nowy)
 * dostaje barwę z `TASK_STATUS_TONES`.
 */
const STATUS_PILL: Partial<Record<string, PillTone>> = {
  // zgłoszenie z portalu klienta czeka na przyjęcie (17.10.2026) — akcent jak nowa rzecz do przejrzenia
  request: 'progress',
  todo: 'neutral',
  in_progress: 'progress',
  blocked: 'waiting',
  review: 'review',
  done: 'done',
};

export function taskStatusTone(status: TaskSummary['status']): PillTone {
  return STATUS_PILL[status] ?? (TASK_STATUS_TONES as Record<string, PillTone | undefined>)[status] ?? 'neutral';
}

/** Status zadania jako pigułka (lista, karta zadania, profil klienta). */
export function TaskStatusLabel({ status, className }: { status: TaskSummary['status']; className?: string }) {
  return (
    <StatusPill tone={taskStatusTone(status)} icon={status === 'done' ? Check : undefined} className={className}>
      {(TASK_STATUS_LABELS as Record<string, string>)[status] ?? status}
    </StatusPill>
  );
}

/** Podpowiedź plakietki „Od klienta” i karty zadania: co z zadania widzi klient w portalu (przegląd portalu 17.10.2026). */
export const FROM_CLIENT_HINT =
  'Zgłoszenie z portalu klienta — klient widzi swój tytuł i opis z chwili zgłoszenia, status i rozmowę. Zmiany tytułu i opisu tutaj są wewnętrzne.';

/**
 * Plakietka „Od klienta” (DECISIONS 2026-10-17 [P] „Model portalu klienta” nr 3): zadanie jest zgłoszeniem z portalu
 * klienta (`origin = 'client_portal'`) — klient widzi swoją treść zgłoszenia, status i rozmowę. Lista, tablica i profil
 * klienta.
 */
export function FromClientBadge({ task, className }: { task: Pick<TaskSummary, 'origin'>; className?: string }) {
  if (task.origin !== 'client_portal') return null;
  return (
    <span
      title={FROM_CLIENT_HINT}
      className={cn(
        'inline-flex h-5 shrink-0 items-center gap-1 rounded-sm bg-soft px-1.5 text-caption leading-none font-medium whitespace-nowrap text-fg-secondary',
        className,
      )}
    >
      <MessageSquareText size={12} strokeWidth={2} aria-hidden />
      Od klienta
    </span>
  );
}

/**
 * Miejsce znacznika „Gotowe” przy zgłoszeniu od klienta (status „Zgłoszenie”): przerywane kółko bez akcji —
 * zgłoszenie najpierw się przyjmuje (`AcceptRequestButton`), a nie kończy jednym kliknięciem (przegląd portalu
 * 17.10.2026: „Gotowe” wysyłało klientowi mail „zakończone”, a „Cofnij” do „Zgłoszenia” nie przechodziło).
 */
export function RequestMark({ size = 20, className }: { size?: 18 | 20; className?: string }) {
  return (
    <span
      aria-hidden
      title="Zgłoszenie od klienta — najpierw przyjmij"
      className={cn(
        'inline-flex shrink-0 rounded-full border-[1.5px] border-dashed border-line-strong',
        size === 20 ? 'size-5' : 'size-[18px]',
        className,
      )}
    />
  );
}

/** „Przyjmij” przy zgłoszeniu od klienta w wierszu listy (nad linkiem wiersza; pole dotyku 44 px na telefonie). */
export function AcceptRequestButton({
  task,
  onAccept,
  className,
}: {
  task: Pick<TaskSummary, 'title'>;
  onAccept: () => void;
  className?: string;
}) {
  return (
    <Button
      size="s"
      variant="secondary"
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onAccept();
      }}
      aria-label={`Przyjmij zgłoszenie: ${task.title}`}
      className={cn('z-10 shrink-0', TOUCH_BUTTON, className)}
    >
      Przyjmij
    </Button>
  );
}

/** Priorytet tylko gdy coś znaczy (wysoki, pilny) — z etykietą (karta zadania). */
export function PriorityStatus({ priority, className }: { priority: TaskSummary['priority']; className?: string }) {
  if (priority !== 'high' && priority !== 'urgent') return null;
  return (
    <Status tone={TASK_PRIORITY_TONES[priority]} className={className}>
      {priority === 'urgent' ? 'Pilne' : `Priorytet: ${TASK_PRIORITY_LABELS[priority].toLowerCase()}`}
    </Status>
  );
}

/** Flaga priorytetu tylko dla wysokiego (bursztyn) i pilnego (czerwień) — wiersze i karty (lifting 17.10.2026). */
export function PriorityFlag({ priority, className }: { priority: TaskSummary['priority']; className?: string }) {
  if (priority !== 'high' && priority !== 'urgent') return null;
  const label = priority === 'urgent' ? 'Pilne' : 'Priorytet: wysoki';
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex shrink-0 items-center',
        priority === 'urgent' ? 'text-danger' : 'text-warning',
        className,
      )}
    >
      <Flag size={14} strokeWidth={2} aria-hidden fill="currentColor" fillOpacity={0.18} />
    </span>
  );
}

/**
 * Okrągły znacznik „Gotowe” (lista, tablica, profil klienta / projektu, Dziś): jedno kliknięcie oznacza zadanie
 * jako gotowe (albo cofa). Pole dotyku 44 × 44 px przy kółku 18–20 px (ujemny margines, układ się nie zmienia);
 * przy najechaniu kółko zielenieje i pokazuje ptaszek. Bez uprawnień do edycji — samo kółko bez akcji.
 */
export function DoneToggle({
  done,
  onToggle,
  disabled,
  title,
  size = 20,
  className,
}: {
  done: boolean;
  onToggle?: () => void;
  disabled?: boolean;
  title: string;
  size?: 18 | 20;
  className?: string;
}) {
  const circle = (
    <span
      aria-hidden
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full border-[1.5px] transition-colors',
        size === 20 ? 'size-5' : 'size-[18px]',
        done
          ? 'border-success bg-success text-on-accent'
          : 'border-line-strong bg-canvas text-transparent group-hover:border-success group-hover:bg-success-subtle group-hover:text-success group-focus-visible:border-success',
      )}
    >
      <Check size={size === 20 ? 12 : 11} strokeWidth={2.75} />
    </span>
  );
  if (!onToggle || disabled) return <span className={cn('inline-flex shrink-0', className)}>{circle}</span>;
  return (
    <button
      type="button"
      data-done-toggle=""
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onToggle();
      }}
      // tablica: przeciąganie karty nie startuje z kółka
      onPointerDown={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
      aria-label={done ? `Przywróć do zrobienia: ${title}` : `Oznacz jako gotowe: ${title}`}
      aria-pressed={done}
      className={cn(
        'group inline-flex shrink-0 items-center justify-center rounded-full outline-none',
        size === 20 ? '-m-3 p-3' : '-m-[13px] p-[13px]',
        'focus-visible:outline-2 focus-visible:-outline-offset-4 focus-visible:outline-accent',
        className,
      )}
    >
      {circle}
    </button>
  );
}
